import { useState, useRef, useCallback, useEffect } from 'react'

export type VoiceRecorderState = 'idle' | 'recording' | 'processing'

type PermissionStatus = 'prompt' | 'granted' | 'denied'

interface UseVoiceRecorderReturn {
  /** Current state of the recorder */
  state: VoiceRecorderState
  /** Recording duration in seconds */
  duration: number
  /** Start recording audio from microphone */
  startRecording: () => Promise<void>
  /** Stop recording and return the audio blob */
  stopRecording: () => Promise<Blob | null>
  /** Cancel recording, discard audio */
  cancelRecording: () => void
  /** Set state to processing (when sending to API) */
  setProcessing: () => void
  /** Reset to idle state */
  reset: () => void
  /** Error message if recording failed */
  error: string | null
  /** Microphone permission status */
  permissionStatus: PermissionStatus
}

export function useVoiceRecorder(): UseVoiceRecorderReturn {
  const [state, setState] = useState<VoiceRecorderState>('idle')
  const [duration, setDuration] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [permissionStatus, setPermissionStatus] = useState<PermissionStatus>('prompt')

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const resolveStopRef = useRef<((blob: Blob | null) => void) | null>(null)
  const permissionRequestedRef = useRef(false)

  // Check permission status on mount (if supported)
  useEffect(() => {
    const checkPermission = async () => {
      try {
        // Check if Permissions API is supported
        if (!navigator.permissions || !navigator.permissions.query) {
          // If not supported, assume prompt until user interaction
          return
        }

        const result = await navigator.permissions.query({ name: 'microphone' as PermissionName })
        setPermissionStatus(result.state as PermissionStatus)

        // Listen for permission changes
        result.addEventListener('change', () => {
          setPermissionStatus(result.state as PermissionStatus)
        })
      } catch (err) {
        // Permissions API not fully supported, ignore
        console.log('Permissions API not supported:', err)
      }
    }

    checkPermission()
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTimer()
      releaseStream()
    }
  }, [])

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const releaseStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
  }, [])

  const startRecording = useCallback(async () => {
    try {
      setError(null)
      chunksRef.current = []

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      // Update permission status after successful access
      if (!permissionRequestedRef.current) {
        setPermissionStatus('granted')
        permissionRequestedRef.current = true
      }

      // Choose best available MIME type
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')
          ? 'audio/ogg;codecs=opus'
          : 'audio/webm'

      const mediaRecorder = new MediaRecorder(stream, { mimeType })
      mediaRecorderRef.current = mediaRecorder

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data)
        }
      }

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType })
        if (resolveStopRef.current) {
          resolveStopRef.current(blob)
          resolveStopRef.current = null
        }
      }

      mediaRecorder.onerror = () => {
        setError('Ошибка записи')
        setState('idle')
        releaseStream()
        stopTimer()
      }

      mediaRecorder.start(250) // collect data every 250ms
      setState('recording')
      setDuration(0)

      // Start duration timer
      const startTime = Date.now()
      timerRef.current = setInterval(() => {
        setDuration(Math.floor((Date.now() - startTime) / 1000))
      }, 500)
    } catch (err) {
      console.error('Microphone access error:', err)

      // Update permission status
      setPermissionStatus('denied')

      // User-friendly error messages
      let message = 'Не удалось получить доступ к микрофону'

      if (err instanceof DOMException) {
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          message = 'Доступ к микрофону запрещён. Разрешите доступ в настройках браузера.'
        } else if (err.name === 'NotFoundError') {
          message = 'Микрофон не найден'
        } else if (err.name === 'NotReadableError') {
          message = 'Микрофон уже используется другим приложением'
        }
      }

      setError(message)
      setState('idle')
    }
  }, [releaseStream, stopTimer])

  const stopRecording = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      stopTimer()

      const mediaRecorder = mediaRecorderRef.current
      if (!mediaRecorder || mediaRecorder.state !== 'recording') {
        releaseStream()
        resolve(null)
        return
      }

      resolveStopRef.current = (blob) => {
        releaseStream()
        resolve(blob)
      }

      mediaRecorder.stop()
    })
  }, [stopTimer, releaseStream])

  const cancelRecording = useCallback(() => {
    stopTimer()

    const mediaRecorder = mediaRecorderRef.current
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.onstop = null // prevent blob creation
      mediaRecorder.stop()
    }

    releaseStream()
    chunksRef.current = []
    setState('idle')
    setDuration(0)
    resolveStopRef.current = null
  }, [stopTimer, releaseStream])

  const setProcessing = useCallback(() => {
    setState('processing')
  }, [])

  const reset = useCallback(() => {
    setState('idle')
    setDuration(0)
    setError(null)
  }, [])

  return {
    state,
    duration,
    startRecording,
    stopRecording,
    cancelRecording,
    setProcessing,
    reset,
    error,
    permissionStatus,
  }
}
